import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log dynamic import failures specifically
    if (error.message?.includes('Failed to fetch dynamically imported module') || 
        error.message?.includes('Loading chunk') ||
        error.name === 'ChunkLoadError') {
      console.error('Dynamic import failure detected. This may be a build/deployment issue.');
      // Could trigger a page reload or show specific error message
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full border-4 border-foreground bg-card shadow-neo p-8 text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-destructive/20 border-4 border-destructive">
              <AlertTriangle className="w-10 h-10 text-destructive" strokeWidth={3} />
            </div>
            
            <div className="space-y-2">
              <h1 className="font-heavy text-3xl uppercase text-foreground">
                System Overload
              </h1>
              <p className="text-muted-foreground font-body">
                You've wandered into a Dungeon Break. Something unexpected happened.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-muted border-2 border-border rounded p-3 text-left">
                <p className="text-xs font-mono text-muted-foreground break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="border-2 border-foreground shadow-neo font-heavy uppercase"
              >
                <Home className="w-4 h-4 mr-2" />
                Return to Safety
              </Button>
              <Button
                onClick={this.handleReload}
                className="border-2 border-foreground shadow-neo font-heavy uppercase bg-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
